import React, { useCallback, useEffect, useRef, useState } from "react";
import { Flex, Input, InputRef, Typography } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { OTP, REGEX, ROUTES } from "../../../constants";
import { generateHexUdid } from "../../../utils";
import { useOtpPaste } from "../../../hooks/useOtpPaste";
import { authService } from "../../../services/authService";
import { storageService } from "../../../services/storageService";

import "./index.scss";

interface LocationState {
  email?: string;
}

const { Text, Title } = Typography;

const VerifyEmailOTP: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const email = (state as LocationState | null)?.email;

  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState<number>(OTP.RESEND_SECS);

  const [otp, setOtp] = useState<string[]>(new Array(OTP.OTP_LENGTH).fill(""));
  const otpResetRef = useRef<() => void>(() => {});

  const inputRefs = useRef<InputRef[]>([]);
  const isSubmitting = useRef(false);
  const focusAt = useCallback((i: number) => inputRefs.current[i]?.focus(), []);

  const handleSubmitOtp = useCallback(
    async (code: string[]) => {
      if (!email) return;
      setLoading(true);

      const udid = generateHexUdid();
      const accessCode = code.join("");
      try {
        const { status, data } = await authService.verifyEmailOtp({
          email,
          udid: udid,
          osType: "4",
          pnTok: "adf",
          pnvTok: "asdf",
          otp: accessCode,
          appVersion: "1.1",
          deviceName: "1212",
        });

        if (status === 200 || status === 201) {
          storageService.persistSession(
            udid,
            data.data,
            data.isPhoneRegistered,
            data.isPurchasedNumber,
          );
          if (data.signup) {
            navigate(ROUTES.PROFILE_SETUP, { replace: true });
          } else {
            window.location.href = ROUTES.HOME;
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ||
            "There was an error. Please try again",
        );
        otpResetRef.current();
      } finally {
        setLoading(false);
      }
    },
    [email, navigate],
  );

  const handleResend = useCallback(async () => {
    if (loading || timer > 0 || !email) return;
    setLoading(true);

    try {
      const { status } = await authService.resendOtp(email);

      if (status === 200) {
        toast.info("OTP has been resent successfully");
        setTimer(OTP.RESEND_SECS);
        setOtp(Array(OTP.OTP_LENGTH).fill(""));
        isSubmitting.current = false;
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "There was an error. please try again");
    } finally {
      setLoading(false);
    }
  }, [loading, timer, email]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      if (loading) return;

      const value = e.target.value;
      if (!REGEX.ONLY_DIGITS.test(value)) return;

      const newOtp = [...otp];
      newOtp[index] = value.slice(-1);
      setOtp(newOtp);

      if (e.target.value && index < OTP.OTP_LENGTH - 1) {
        focusAt(index + 1);
      }

      const isOtpComplete = newOtp.every((digit) => digit !== "");
      if (isOtpComplete) {
        handleSubmitOtp(newOtp);
      }
    },
    [otp, loading, focusAt, handleSubmitOtp],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (loading) return;
      if (e.key === "Backspace" && !otp[index] && index > 0) {
        e.preventDefault();
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        focusAt(index - 1);
      } else if (e.key === "Enter") {
        const isOtpComplete = otp.every((digit) => digit !== "");
        if (isOtpComplete) {
          handleSubmitOtp(otp);
        }
      }
    },
    [loading, otp, focusAt, handleSubmitOtp],
  );

  const { handlePaste } = useOtpPaste({
    loading,
    isSubmitting,
    inputRefs,
    setOtp,
    onComplete: handleSubmitOtp,
  });

  useEffect(() => {
    if (!email) {
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (timer <= 0) return;

    const timeout = setTimeout(() => {
      setTimer(timer - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [timer]);

  return (
    <div className="verify-email-otp-container">
      <div>
        <img src="/assets/images/lock-icon.svg" alt="" />
      </div>

      <Title className="page-title">Verify OTP</Title>

      <Text className="sub-text">
        An authentication code has been sent to email
      </Text>

      <Flex gap={16} align="center" className="otp-wrapper">
        <Flex gap={12}>
          {otp.map((digit, index) => (
            <Input
              key={index}
              value={digit}
              maxLength={1}
              disabled={loading}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={(e) => handlePaste(e, index)}
              className="otp-input"
              ref={(el) => {
                if (el) inputRefs.current[index] = el;
              }}
            />
          ))}
        </Flex>
      </Flex>

      <Text className="recieve-otp">
        Didn't receive OTP code? <br />
        <span className="resend" onClick={handleResend}>
          Resend OTP {timer > 0 && `in ${timer}s`}
        </span>
      </Text>

      <div id="recaptcha-container"></div>
    </div>
  );
};

export default VerifyEmailOTP;
